import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Drawer, Toggle } from '../ui';
import { settings as settingsApi } from '../../services/api';

const USER_ROLES = [
  { value: 'admin',      label: 'Admin',      desc: 'Full access including settings and user management' },
  { value: 'finance',    label: 'Finance',    desc: 'Returns, withdrawals, journals, and reports' },
  { value: 'operations', label: 'Operations', desc: 'Day-to-day data entry and read access to most modules' },
  { value: 'executive',  label: 'Executive',  desc: 'Read-only dashboards and portfolio reports' },
];

const roleBadgeType = (role) => {
  if (role === 'admin') return 'active';
  if (role === 'finance') return 'info';
  if (role === 'executive') return 'neutral';
  return 'warn';
};

export function UsersSection({ currentUser, showToast }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    email: '', fullName: '', password: '', role: 'operations', isActive: true,
  });
  const [pwForm, setPwForm] = useState({ password: '', confirmPassword: '' });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.users();
      setUsers(res.data || []);
    } catch (e) {
      showToast(e.message || 'Could not load users', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { reload(); }, [reload]);

  const uf = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const close = () => {
    setAddOpen(false);
    setEditUser(null);
    setForm({ email: '', fullName: '', password: '', role: 'operations', isActive: true });
    setPwForm({ password: '', confirmPassword: '' });
  };

  const openAdd = () => {
    setForm({ email: '', fullName: '', password: '', role: 'operations', isActive: true });
    setAddOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      email: u.email,
      fullName: u.full_name,
      password: '',
      role: u.role,
      isActive: u.is_active === 1 || u.is_active === true,
    });
    setPwForm({ password: '', confirmPassword: '' });
  };

  const createUser = async () => {
    if (!form.email?.trim() || !form.fullName?.trim() || !form.password) {
      showToast('Email, name and password are required', 'error');
      return;
    }
    if (form.password.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    setSaving(true);
    try {
      await settingsApi.createUser({
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        password: form.password,
        role: form.role,
      });
      showToast('User created');
      close();
      reload();
    } catch (e) {
      showToast(e.message || 'Could not create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateUser = async () => {
    if (!editUser || !form.fullName?.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await settingsApi.updateUser(editUser.id, {
        fullName: form.fullName.trim(),
        role: form.role,
        isActive: form.isActive,
      });
      showToast('User updated');
      close();
      reload();
    } catch (e) {
      showToast(e.message || 'Could not update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!editUser) return;
    if (!pwForm.password || !pwForm.confirmPassword) {
      showToast('Enter and confirm the new password', 'error');
      return;
    }
    if (pwForm.password.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    if (pwForm.password !== pwForm.confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    setPwSaving(true);
    try {
      await settingsApi.resetUserPassword(editUser.id, pwForm.password);
      setPwForm({ password: '', confirmPassword: '' });
      showToast('Password reset successfully');
    } catch (e) {
      showToast(e.message || 'Could not reset password', 'error');
    } finally {
      setPwSaving(false);
    }
  };

  const roleDesc = USER_ROLES.find((r) => r.value === form.role)?.desc;

  return (
    <>
      <div className="settings-sect">
        <div className="ss-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="ss-title">Platform users</div>
          <button type="button" className="btn btn-p btn-sm" onClick={openAdd}>+ Add user</button>
        </div>
        <div className="ss-body">
          <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
            Internal accounts for staff. Choose a user type (role) to control what each person can access.
          </p>
          {loading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>User type</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {!users.length && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                        No users yet — add your first team member
                      </td>
                    </tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{u.email}</td>
                      <td>
                        <Badge type={roleBadgeType(u.role)}>
                          {USER_ROLES.find((r) => r.value === u.role)?.label || u.role}
                        </Badge>
                      </td>
                      <td>
                        <Badge type={u.is_active ? 'active' : 'neutral'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 11.5, color: 'var(--text3)' }}>
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-ZA') : '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-o btn-sm"
                          onClick={() => openEdit(u)}
                          disabled={u.id === currentUser?.id}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Drawer open={addOpen} onClose={close} title="Add platform user">
        <div style={{ background: 'var(--brand-light)', borderRadius: 7, padding: '10px 12px', marginBottom: 16, fontSize: 12, color: 'var(--brand-dark)' }}>
          Creates a login for a team member. They sign in with the email and password you set here.
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">Full name *</label>
            <input className="field-input" value={form.fullName} onChange={uf('fullName')} placeholder="e.g. Jane Doe" />
          </div>
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">Email *</label>
            <input className="field-input" type="email" value={form.email} onChange={uf('email')} placeholder="name@huhslcap.co.za" />
          </div>
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">Password *</label>
            <input
              className="field-input"
              type="password"
              value={form.password}
              onChange={uf('password')}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">User type *</label>
            <select className="field-sel" value={form.role} onChange={uf('role')}>
              {USER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {roleDesc && (
              <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>{roleDesc}</p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-p" onClick={createUser} disabled={saving}>
            {saving ? 'Saving…' : 'Create user'}
          </button>
          <button type="button" className="btn" onClick={close}>Cancel</button>
        </div>
      </Drawer>

      <Drawer open={!!editUser} onClose={close} title={`Edit user — ${editUser?.full_name || ''}`}>
        <div className="form-row">
          <div>
            <label className="field-label">Full name *</label>
            <input className="field-input" value={form.fullName} onChange={uf('fullName')} />
          </div>
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">Email</label>
            <input className="field-input" type="email" value={form.email} disabled style={{ opacity: 0.7 }} />
          </div>
        </div>
        <div style={{ background: 'var(--brand-light)', borderRadius: 7, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: 'var(--brand-dark)' }}>
          Stored passwords are encrypted and cannot be viewed. Use reset below to set a new password.
        </div>
        <div className="form-row">
          <div>
            <label className="field-label">User type *</label>
            <select
              className="field-sel"
              value={form.role}
              onChange={uf('role')}
              disabled={editUser?.id === currentUser?.id}
            >
              {USER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {editUser?.id === currentUser?.id && (
              <p style={{ fontSize: 11, color: 'var(--warning)', marginTop: 6 }}>You cannot change your own role.</p>
            )}
            {roleDesc && (
              <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>{roleDesc}</p>
            )}
          </div>
        </div>
        <div className="form-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <Toggle
              on={!!form.isActive}
              onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
              disabled={editUser?.id === currentUser?.id}
            />
            <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>Account active (can sign in)</span>
          </div>
        </div>
        <div className="form-row fr2">
          <div>
            <label className="field-label">New password</label>
            <input
              className="field-input"
              type="password"
              value={pwForm.password}
              onChange={(e) => setPwForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="field-label">Confirm password</label>
            <input
              className="field-input"
              type="password"
              value={pwForm.confirmPassword}
              onChange={(e) => setPwForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div style={{ marginTop: 6 }}>
          <button type="button" className="btn btn-o btn-sm" onClick={resetPassword} disabled={pwSaving || saving}>
            {pwSaving ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-p" onClick={updateUser} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="btn" onClick={close}>Cancel</button>
        </div>
      </Drawer>
    </>
  );
}
